/* Legato — the separation animation on the homepage.

   This is the Wavelength lab's treatment 14 (variant-o.js), taken verbatim from
   the prototype apart from this header. Treatment 14 is treatment 13 -- what
   the prototype's own homepage runs -- with one thing changed: the orange voice
   line keeps its shape throughout instead of compressing into a band at the
   last beat, and it bolds as it grows rather than thinning.

   The four beat thresholds here (0, 0.28, 0.52, 0.76) are the same ones the
   section's caption crossfade uses. Change one and you must change the other,
   or the words stop agreeing with the picture.

   Deterministic: the same scroll position always draws the same frame.
*/

/* Variant O — treatment 13's room, treatment 07's voice.

   Everything about the field, the beats and the chart is treatment 13, which is
   what production runs. The only thing swapped is how the orange line behaves,
   and it now behaves the way "Steve v1" (treatment 07) did:

     · its shape NEVER changes. 13 compresses the carrier and widens the
       envelopes at beat 4 until the bursts merge into a continuous band; that
       reads as the voice changing pitch. Here evalLineO is called with no
       multipliers, so only amplitude and stroke weight move — the same voice,
       amplified.
     · no roughness. 13 hands the voice over gritty at beat 2 and resolves it
       at beat 3. 07 has no such stage, so the grit term is gone.
     · it thickens instead of thinning. 13 finishes at a 1.35 stroke because a
       compressed band needs a thin line to stay legible. With no compression
       there is nothing to keep thin, so the line bolds to ~4.5 as it grows.

   The room is untouched from 13: same flatten at beat 2, same lanes measured
   off the table's real rows every frame, same four thresholds. So this is a
   clean A/B on the voice alone.

     1  0.00  Sound from everything, coming at you from everywhere
     2  0.28  The noise lines flatten; the voice stays orange and steady
     3  0.52  The voice grows in amplitude
     4  0.76  It keeps growing and bolding — no compression                */

function mulberry32O(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeLineO(seed, o) {
  const rnd = mulberry32O(seed);
  const packets = [];
  let x = rnd() * 0.12;
  while (x < 1.04) {
    packets.push({
      c: x,
      w: o.width * (0.7 + rnd() * 0.6),
      a: 0.45 + rnd() * 0.55,
      f: o.freq * (0.82 + rnd() * 0.36),
      ph: rnd() * Math.PI * 2,
      drift: 0.4 + rnd() * 1.2,
    });
    x += o.gap * (0.65 + rnd() * 0.8);
  }
  return { packets, lane: o.lane, speech: !!o.speech, colour: o.colour, label: o.label };
}

function evalLineO(line, t, time, fmul, wmul) {
  fmul = fmul || 1; wmul = wmul || 1;
  let v = 0;
  for (const p of line.packets) {
    const w = p.w * wmul;
    const d = (t - p.c) / w;
    if (d < -3 || d > 3) continue;
    v += Math.sin((t - p.c) * Math.PI * 2 * p.f * fmul + p.ph + time * p.drift) * Math.exp(-d * d) * p.a;
  }
  return v;
}

/* Desaturated and close in value, so they read as one family and the orange
   is the only thing that jumps out of the chart. */
const SOURCES_O = [
  { lane: -0.78, freq: 22, width: 0.085, gap: 0.20, seed: 11,
    colour: '138,124,104', label: 'Music overhead' },
  { lane: -0.50, freq: 54, width: 0.020, gap: 0.12, seed: 23,
    colour: '150,137,122', label: 'Glasses and cutlery' },
  { lane: -0.22, freq: 14, width: 0.055, gap: 0.42, seed: 31,
    colour: '104,117,117', label: 'Chairs moving' },
  { lane:  0.00, freq: 30, width: 0.052, gap: 0.14, seed: 47, speech: true,
    colour: '255,78,10', label: 'Your spouse asking about your day' },
  { lane:  0.22, freq: 10, width: 0.030, gap: 0.19, seed: 59,
    colour: '135,125,155', label: 'People walking past' },
  { lane:  0.50, freq: 34, width: 0.048, gap: 0.16, seed: 71,
    colour: '110,139,122', label: 'The next table talking' },
  { lane:  0.78, freq: 64, width: 0.140, gap: 0.30, seed: 83,
    colour: '160,146,128', label: 'Room tone' },
];
const LINES_O = SOURCES_O.map(s => makeLineO(s.seed, s));
const SPEECH_O = LINES_O.find(l => l.speech);

function buildTableO(root) {
  if (!root) return [];
  root.innerHTML = '';
  return LINES_O.map(line => {
    const row = document.createElement('div');
    row.className = 'tbl-row' + (line.speech ? ' is-voice' : '');
    const sw = document.createElement('span');
    sw.className = 'tbl-sw';
    sw.style.background = 'rgb(' + line.colour + ')';
    const name = document.createElement('span');
    name.className = 'tbl-name';
    name.textContent = line.label;
    row.append(sw, name);
    root.appendChild(row);
    return row;
  });
}

function initWaveO(canvas, opts) {
  const ctx = canvas.getContext('2d');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let W = 0, H = 0, dpr = 1, progress = 0;
  const t0 = performance.now();
  const clamp01 = v => (v < 0 ? 0 : v > 1 ? 1 : v);
  const seg = (v, a, b) => clamp01((v - a) / (b - a));
  const easeOut = t => 1 - Math.pow(1 - t, 3);
  const easeInOut = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  const tableRoot = opts && opts.table;
  const rows = buildTableO(tableRoot);
  let lastDim = -1;

  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    const r = canvas.getBoundingClientRect();
    W = r.width; H = r.height;
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /* Where each line should end up: the centre of its own table row, in canvas
     coordinates. Falls back to evenly spread lanes when the table is stacked
     underneath (narrow screens) rather than sitting beside the chart. */
  function targets() {
    const cr = canvas.getBoundingClientRect();
    const beside = rows.length && rows[0].getBoundingClientRect().left > cr.right - 8;
    if (!beside) {
      const span = Math.min(H * 0.78, 250);
      return LINES_O.map((l, i) =>
        ({ y: H / 2 + (i - (LINES_O.length - 1) / 2) * (span / (LINES_O.length - 1)), beside: false }));
    }
    return rows.map(row => {
      const rr = row.getBoundingClientRect();
      return { y: rr.top + rr.height / 2 - cr.top, beside: true };
    });
  }

  function draw(now) {
    const time = reduced ? 0 : (now - t0) / 1000;
    if (opts && opts.getProgress) progress = clamp01(opts.getProgress());
    const p = progress;
    ctx.clearRect(0, 0, W, H);
    if (!W || !H) { requestAnimationFrame(draw); return; }

    const part     = easeInOut(seg(p, 0.22, 0.48));  /* lanes find their rows  */
    const quiet    = easeInOut(seg(p, 0.28, 0.52));  /* 2—the room goes flat */
    const grow     = easeInOut(seg(p, 0.52, 0.78));  /* 3—the voice swells   */
    const clean    = easeInOut(seg(p, 0.50, 0.80));  /* 3—roughness resolves */
    const amplify  = easeInOut(seg(p, 0.76, 1.00));  /* 4—it tightens        */

    const cy = H / 2;
    const tgt = targets();
    const beside = tgt.length ? tgt[0].beside : false;

    /* the last stretch before the table is kept clear of wiggle so every line
       arrives flat into its row, like a lead-in on a schematic */
    /* Full bleed: the lines run the whole width and carry on behind the table
       rather than stopping at it, so the chart sits over the room instead of
       bounding it. */
    const waveW = W;

    const baseAmp = Math.min(H * 0.085, 30);
    const tangle = 1.7 - part * 0.7;
    /* Beat 2: the room is silenced, not removed. Amplitude collapses to 4% so
       every source flattens in place and stays legible against its own row. */
    const noiseAmp = baseAmp * tangle * (1 - quiet * 0.96);
    /* The voice holds through beat 2 instead of unwinding with the tangle.
       Sharing `tangle` with the room cost it a fifth of its height exactly
       where the brief says to keep it, so it gets its own, gentler unwind. */
    const voiceTangle = 1.7 - part * 0.22;
    /* Beat 3 grows it and beat 4 keeps growing it — 13 spent beat 4 tightening
       instead, which is the difference this treatment exists to show. */
    const speechAmp = baseAmp * voiceTangle * (1 + grow * 0.95 + amplify * 1.15);

    /* No fmul/wmul and no grit: treatment 07's rule is that the voice's
       frequency and envelope never change, so there is nothing here to
       compress and nothing to resolve. Without compression the carrier stays
       well under the sampling limit, so every line can step at 2px. */

    LINES_O.forEach((line, i) => {
      const isVoice = !!line.speech;
      const alpha = isVoice
        /* 07's curve: it starts a touch softer and lifts as it grows */
        ? 0.62 + part * 0.20 + amplify * 0.18
        /* dimmed as they flatten, but never to nothing—a silent source is
           still a source, and the chart beside it still names it */
        : (0.62 - Math.abs(line.lane) * 0.10) * (1 - quiet * 0.55);
      if (alpha <= 0.004) return;

      /* tangled in the middle at the start, arriving at its own row by 0.56 */
      const startY = cy + line.lane * Math.min(H * 0.07, 20);
      const endY = tgt[i].y;
      const baseY = startY + (endY - startY) * part;
      const amp = isVoice ? speechAmp : noiseAmp;
      /* heavier at the end, not thinner: 1.4 -> ~4.5 against the room's 1.1 */
      const weight = isVoice ? 1.4 + part * 0.5 + amplify * 2.6 : 1.1;
      const step = 2;

      ctx.strokeStyle = 'rgba(' + line.colour + ',' + alpha.toFixed(3) + ')';
      ctx.lineWidth = weight;
      ctx.lineJoin = 'round'; ctx.lineCap = 'round';
      ctx.beginPath();
      for (let x = 0; x <= waveW; x += step) {
        const u = x / waveW;
        /* one call for both: the voice gets no multipliers, so its shape is
           identical to the shape it opened with */
        const y = baseY + evalLineO(line, u, time) * amp;
        if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    });

    /* the table dims as the room goes flat, so the chart and the picture always
       say the same thing */
    const dim = Math.round(quiet * 20);
    if (dim !== lastDim) {
      lastDim = dim;
      rows.forEach(row => {
        if (row.classList.contains('is-voice')) return;
        row.style.opacity = (1 - quiet * 0.74).toFixed(2);
      });
    }

    if (opts && opts.onProgress) opts.onProgress(p);
    requestAnimationFrame(draw);
  }

  resize();
  window.addEventListener('resize', resize);
  requestAnimationFrame(draw);
  return { resize };
}
